/*
The explicit approval lock (XOL-596, Carlos 2026-10-13; API: XOL-595 plan 4.9).

Nothing locks by itself any more. A step's approval is locked only by the lock
commands (`POST /step/{id}/lock`, `complete {lock: true}`) and unlocked by
`POST /step/{id}/unlock`; the lock is a SERVER-OWNED flag on the party-1 step row
(`approvalLocked`, `approvalLocked_by`, `approvalLocked_at`, requestRowSync
STEP_OWNED_KEYS). This module holds the pure rules the drawer, the step components
and the PRG forms share, and the two prompts (`askLock`, `askUnlock`; see the
bottom).

  approvalStepKind(action, step)   'step2' | 'step3' | 'step5' | 'prg' | null: which
                                   approval, if any, a step row has (the API's
                                   StepApprovalRules::kindFor)
  isDecided(kind, row)             is there a decision to lock? `row` is the BASELINE
                                   (what the server last said), never the v-model
  expectedBody(kind, row)          the decision the user saw, sent with /lock and
                                   complete {lock: true} (UF-L2): the API answers 409
                                   stale_decision when it no longer matches
  decisionText(kind, row, action)  "Approved" / "Rejected" / "Conditional" /
                                   "{Agreement Type} approved" / "Approved"
  stepTitle(action, step)          the step's name ("Technical Proposal")
  lockedByMigration(row)           locked with no person (the one-time API migration)
  canLock / canUnlock / canDecideFinalStep   who may lock, who may unlock
  canDecide(vm, action, step)      who may change the decision (the decision fields' gate)
  askLock(root, opts)              the prompt (drawer close, Complete Step, PRG Save):
                                   Promise<'lock' | 'skip' | 'cancel'>
  askUnlock(root, opts)            the Unlock confirm: Promise<'unlock' | 'cancel'>

ES5, no optional chaining or nullish coalescing.
------------------------------------------------------------------------ */

import Vue from 'vue'
import { copyText } from '@/lib/requestSyncCopy'
import ApprovalLockDialog from '@/components/requests/ApprovalLockDialog'

// The approval rows: party 1 of an RTS / RTU / RTB / RTD request (steps 2, 3 and 5)
// and of a PRG (steps 1 and 3). The API's StepApprovalRules::kindFor says the same.
var RT_TYPES = ['RTS', 'RTU', 'RTB', 'RTD']
var RT_KINDS = { 2: 'step2', 3: 'step3', 5: 'step5' }
var PRG_STEPS = [1, 3]

export function approvalStepKind (action, step) {
  if (!step || Number(step.partyNo) !== 1) {
    return null
  }

  var type = String((action && action.actionType) || step.actionType || '').toUpperCase()
  var stepNo = Number(step.stepNo)

  if (type === 'PRG') {
    return PRG_STEPS.indexOf(stepNo) !== -1 ? 'prg' : null
  }

  if (RT_TYPES.indexOf(type) !== -1) {
    return RT_KINDS[stepNo] || null
  }

  return null
}

function statusOf (row) {
  var s = row && row.approvedStatus !== undefined && row.approvedStatus !== null ? String(row.approvedStatus).trim() : ''
  return s
}

// "Decided" per the contract: steps 2 and 5 have a status that is not blank and not
// Pending (Approved, Rejected and Conditional all count); step 3 and PRG are ticked.
export function isDecided (kind, row) {
  if (!kind || !row) {
    return false
  }

  if (kind === 'step3' || kind === 'prg') {
    return !!row.approved
  }

  var status = statusOf(row)

  return status !== '' && status.toLowerCase() !== 'pending'
}

// The body of /lock and of complete {lock: true}: the decision the user saw (NULL is
// Pending / not ticked on the server). Always from the baseline.
export function expectedBody (kind, row) {
  if (kind === 'step3' || kind === 'prg') {
    return { expectedApproved: !!(row && row.approved) }
  }

  var status = statusOf(row)

  return { expectedApprovedStatus: status === '' ? null : status }
}

export function decisionText (kind, row, action) {
  if (kind === 'step3') {
    var doc = action && action.state && action.state.step3 ? action.state.step3.docType : null

    return copyText('requestSync.approvalLock.decision.legal', { docType: doc })
  }

  if (kind === 'prg') {
    return copyText('requestSync.approvalLock.decision.prg')
  }

  return statusOf(row)
}

// The workflow's own name for the step ("Technical Proposal"), as the drawer's
// stepper shows it; the step row carries it as `stepName`.
export function stepTitle (action, step) {
  return (step && step.stepName) || ''
}

// Locked by a person, or by the migration that locked every decided approval
// (`approvalLocked_by` NULL): the migration's by/at are never printed.
export function lockedByMigration (row) {
  return !!row && !!row.approvalLocked && !row.approvalLocked_by
}

/* ------------------------------------------------------------------------
   Who may lock, who may unlock (the SPA is the only guard: the API checks Tower
   Co, as it does for approval). `vm` is anything carrying the AccessMixins
   helpers (a component, or the app root).
------------------------------------------------------------------------ */

function isLive (action) {
  return !!action && !(action.status === 'Closed' || action.status === 'Rejected')
}

// May the viewer decide step 5's FINAL approval (XOL-596 UX 15): an approver or a
// manager with access to requests. The step-5 lock follows this gate (UF-L11).
export function canDecideFinalStep (vm) {
  return (vm.isApprover() || vm.isManager()) && vm.canAccess('project')
}

// Who may DECIDE an approval (XOL-596, decision fields): the gate of the controls and
// of the save behind them. Step 2 = an approver with access to requests; step 3 = a
// Tower Co user with finance access; step 5 = the final-approval gate. The SPA is the
// only guard (the API checks Tower Co). A LOCK is not part of it: a locked approval is
// refused by the API with 403 approval_locked, which the card explains.
export function canDecide (vm, action, step) {
  var kind = approvalStepKind(action, step)

  if (!kind || kind === 'prg' || !isLive(action) || !vm.isTowerCo() || vm.isViewOnly()) {
    return false
  }

  if (kind === 'step2') {
    return vm.isApprover() && vm.canAccess('project')
  }

  if (kind === 'step3') {
    return vm.canAccess('finance')
  }

  return canDecideFinalStep(vm)
}

// Who may LOCK a decided approval (Carlos, Q-L5: "who could approve before Feb
// 2026"): step 2 = its select's gate (approver with access to requests); step 3 =
// any Tower Co user who can write; step 5 = today's decide gate (UF-L11); the PRG
// steps = whoever can edit the step. Always a live request and a writing Tower Co.
export function canLock (vm, action, step) {
  var kind = approvalStepKind(action, step)

  if (!kind || !isLive(action) || !vm.isTowerCo() || vm.isViewOnly()) {
    return false
  }

  if (kind === 'step2') {
    return vm.isApprover() && vm.canAccess('project')
  }

  if (kind === 'step5') {
    return canDecideFinalStep(vm)
  }

  return true
}

// Who may UNLOCK (Carlos): an approver or a manager, with the section's access
// (project for steps 2 and 5, finance for step 3). A completed step may be unlocked.
export function canUnlock (vm, action, step) {
  var kind = approvalStepKind(action, step)

  if (!kind || !isLive(action) || !vm.isTowerCo() || vm.isViewOnly()) {
    return false
  }

  if (!(vm.isApprover() || vm.isManager())) {
    return false
  }

  if (kind === 'prg') {
    return true
  }

  return vm.canAccess(kind === 'step3' ? 'finance' : 'project')
}

/* ------------------------------------------------------------------------
   The prompts. ONE dialog component (ApprovalLockDialog), mounted on <body> and
   destroyed again; the promise resolves only AFTER the dialog has closed, so no
   command starts while it is open (Element's MessageBox is one instance with a
   queue: a hang dialog opened by the command would queue behind a prompt that is
   still up).
------------------------------------------------------------------------ */

function mountDialog (root, props) {
  return new Promise(function (resolve) {
    var vm = new Vue({
      parent: root && root._isVue ? root : undefined,
      render: function (h) {
        return h(ApprovalLockDialog, {
          props: props,
          on: {
            settled: function (answer) {
              // Let Element finish closing before the element is removed.
              setTimeout(function () {
                var el = vm.$el

                vm.$destroy()
                if (el && el.parentNode) {
                  el.parentNode.removeChild(el)
                }
              }, 0)
              resolve(answer)
            }
          }
        })
      }
    }).$mount()

    document.body.appendChild(vm.$el)
  })
}

/**
 * Ask whether to lock a decided, unlocked approval.
 *
 *   opts.mode     'close' | 'complete' | 'prgSave'
 *   opts.rows     close: [{ n, title, decision, date }] (one per approval)
 *   opts.rtx, opts.id   the request, named in the close prompt
 *   opts.decision complete: the decision named in the note
 *   opts.stepNo   complete: the step number named in the body ("Once Step 2 is completed ...")
 *   opts.alive    () => Boolean; false closes the prompt as 'cancel'
 *
 * Resolves 'lock' (Lock / Lock all / Lock & complete / Save and lock), 'skip' (Leave
 * unlocked / Complete without locking / Save without locking) or 'cancel' (Stay /
 * Cancel / Esc / the X).
 */
export function askLock (root, opts) {
  var o = opts || {}

  return mountDialog(root, {
    mode: o.mode,
    rows: o.rows || [],
    rtx: o.rtx || '',
    id: o.id === undefined ? null : o.id,
    decision: o.decision || '',
    stepNo: o.stepNo === undefined ? null : o.stepNo,
    alive: o.alive || null
  })
}

/**
 * The Unlock confirm. opts: { n, title, decision, fields, alive }; resolves 'unlock'
 * or 'cancel'.
 */
export function askUnlock (root, opts) {
  var o = opts || {}

  return mountDialog(root, {
    mode: 'unlock',
    unlock: { n: o.n, title: o.title, decision: o.decision, fields: o.fields },
    alive: o.alive || null
  })
}

// What an Unlock makes editable again, for the confirm's sentence.
export function unlockedFields (kind, action) {
  if (kind === 'step3') {
    var doc = action && action.state && action.state.step3 ? action.state.step3.docType : null

    return copyText('requestSync.approvalLock.fields.step3', { docType: doc })
  }

  return copyText('requestSync.approvalLock.fields.' + (kind === 'step5' ? 'step5' : (kind === 'prg' ? 'prg' : 'step2')))
}
